import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components/macro';
import {
  Divider,
  InputBase as MuiInput,
  MenuItem as MuiMenuItem
} from '@material-ui/core';

import dotProp from 'dot-prop-immutable';

import generateId from '../../utils/generateId';

import { AddCircleOutline as AddCircleIcon } from '@material-ui/icons';
import VisibilityIcon from '@material-ui/icons/VisibilityOutlined';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOffOutlined';
import FlagIcon from '@material-ui/icons/FlagOutlined';

import { unwrapFieldId, wrapFieldId } from '../../utils/getHiddenFields';

import { AddButton } from '../form-builder/FormBuilderFieldForm';
import useFetchWithCallbacks from '../../hooks/useFetchWithCallbacks';
import { compareTypeMap } from './compareTypeMap';
import { InlineSelect } from './InlineSelect';
import { ConditionBuilder } from './ConditionBuilder';
import { defaultComparisonMap } from './defaultComparisonMap';
import { isComparisonValid } from './isComparisonValid';

const styleVisibilityIcon = {
  display: 'inline-block',
  verticalAlign: 'middle',
  marginRight: '0.3ch'
};

const ShowHide = ({ uiType, showHide, onChange }) => (
  <>
    <InlineSelect
      value={showHide}
      onChange={onChange}
      input={<MuiInput />}
      IconComponent={() => null}
      data-testid="logicbuilder-select-show-hide"
      SelectDisplayProps={{ style: { padding: '8px 2px', fontWeight: 700 } }}
    >
      <MuiMenuItem value="show">
        <VisibilityIcon style={styleVisibilityIcon} />
        Show
      </MuiMenuItem>
      <MuiMenuItem value="hide">
        <VisibilityOffIcon style={styleVisibilityIcon} />
        Hide
      </MuiMenuItem>
    </InlineSelect>{' '}
    {uiType === 'section_break'
      ? 'this section'
      : uiType === 'instructional_text'
      ? 'this instructional text'
      : 'this question'}
  </>
);

const ConditionHeader = styled.div`
  padding: 16px;
`;

export const packCondition = condition => {
  const { comparisons = [], andOr: givenAndOr, input } = condition;
  let andOr = givenAndOr;

  if (!input || comparisons.length === 0) {
    return null;
  }

  // Apply De Morgan's laws
  if (comparisons[0].negated) {
    andOr = 'and';
  }

  const output = [];

  comparisons.forEach(
    ({
      op,
      value,
      anytimeOp,
      intervalUnit,
      intervalCount,
      intervalUnit2,
      intervalCount2,
      type
    }) => {
      let newOutputObject = {};
      if (isComparisonValid({ op, value })) {
        if (value === '_FIELD_IS_BLANK') {
          let finalOp = op;
          if (comparisons[0].negated || op === 'IS NOT NULL') {
            finalOp = 'IS NOT NULL';
          } else {
            finalOp = 'IS NULL';
          }
          output.push({
            field: wrapFieldId(input),
            op: finalOp,
            value: '_FIELD_IS_BLANK',
            internOp: andOr
          });
        } else if (op === 'future' || op === 'past') {
          newOutputObject = {
            field: wrapFieldId(input),
            op: op,
            value: 'now',
            internOp: andOr
          };
          output.push(newOutputObject);
        } else {
          let valToPush = {
            field: wrapFieldId(input),
            op,
            value,
            internOp: andOr
          };
          if (intervalUnit) {
            valToPush.intervalUnit = intervalUnit;
          }
          if (intervalCount) {
            valToPush.intervalCount = intervalCount;
          }
          if (type) {
            valToPush.type = type;
          }
          output.push(valToPush);
        }
      }
    }
  );

  if (output.length === 0) {
    return null;
  }

  // if (andOr === 'or') {
  //   output[output.length - 1].internOp = 'or';
  // }

  return output;
};

export const unpackCondition = condition => {
  if (typeof condition === 'string') {
    condition = JSON.parse(condition);
  }

  /**
   * check if the last condition is a single-key object
   * `{ internOp: 'or' }`, and if so,
   * discard it and set `rootAndOr` to `'or'`
   */
  const lastCondition = condition[condition.length - 1];

  let rootAndOr = 'and';

  if (
    !('field' in lastCondition) &&
    !('value' in lastCondition) &&
    lastCondition.internOp &&
    lastCondition.internOp.toLowerCase() === 'or'
  ) {
    rootAndOr = 'or';
    condition.pop();
  }

  /**
   * structure of condition should be
   *
   * ```js
   * [
   *   [
   *     { field: 'input_1' }, { field: 'input_1' }, { field: 'input_1' }
   *   ],
   *   [
   *     { field: 'input_2' }, { field: 'input_2' }
   *  ]
   * ]
   * ```
   */
  const conditions = condition.map(c => ({
    input: unwrapFieldId(c[0].field),
    comparisons: c.map(({ op, value, type, intervalCount, intervalUnit }) => {
      let oneComparison = {
        op,
        negated: op === '<>' || op === 'NOT ILIKE' || op === 'IS NOT NULL',
        value,
        _id: generateId()
      };
      if (type) {
        oneComparison.type = type;
      }
      if (intervalCount) {
        oneComparison.intervalCount = intervalCount;
      }
      if (intervalUnit) {
        oneComparison.intervalUnit = intervalUnit;
      }
      return oneComparison;
    }),
    andOr: c.some(c2 => c2.internOp === 'or') ? 'or' : 'and'
  }));

  // Apply De Morgan's laws

  conditions.forEach(c => {
    if (c.comparisons && c.comparisons[0] && c.comparisons[0].negated) {
      c.andOr = c.andOr === 'or' ? 'and' : 'or';
    }
  });

  return [conditions, rootAndOr];
};

export const conditionsReducer = (state, action) => {
  const { type, i, payload } = action;

  switch (type) {
    case 'update':
      // return state;
      return dotProp.set(state, i, { ...state[i], ...payload });
    // return dotProp.set(state, i, { ...state[i], ...payload });

    case 'add':
      return [...state, { _id: generateId() }];

    case 'remove':
      // return state;
      return state.filter(({ _id }) => _id !== payload.id);

    case 'load':
      return payload.map(condition => ({
        ...condition,
        _id: generateId()
      }));

    default:
      return state;
  }
};

const ConditionalLogic = ({
  // show,
  setShow,
  formId,
  rules = [],
  setRules,
  actionTypes,
  selectShowHide,
  preferSelf,
  internalId,
  uiType,
  deletedField
}) => {
  const [lastUpdate, setLastUpdate] = React.useState(Date.now());
  const needUpdate = () => {
    setLastUpdate(Date.now());
  };

  const [conditions, conditionsDispatch] = React.useReducer(
    conditionsReducer,
    []
  );

  // create default when opening settings with no conditions
  const createDefaultRule = () => {
    if (conditions.length === 0 || (conditions[0] && conditions[0].internOp)) {
      conditionsDispatch({
        type: 'load',
        payload: [
          {}
          // preferSelf && defaultComparisonMap[compareTypeMap[uiType]]
          //   ? {
          //       input: internalId,
          //       comparisons: [defaultComparisonMap[compareTypeMap[uiType]]]
          //     }
          //   : {}
        ]
      });
      needUpdate();
    }
  };

  const [actionType, setActionType] = React.useState(actionTypes[0]);
  const [ruleId, setRuleId] = React.useState(null);
  const [rootAndOr, setRootAndOr] = React.useState('and');

  const fieldId = internalId;

  const rule = rules.find(
    r => r.fieldId === fieldId && actionTypes.includes(r.action.actionType)
  );

  const updateRootAndOr = v => {
    setRootAndOr(v);
    needUpdate();
  };

  const [sentPOST, setSentPOST] = React.useState(false);

  const fetchAPI = useFetchWithCallbacks();

  useEffect(() => {
    conditions.forEach(condition => {
      if (condition.input === deletedField) {
        remove(condition._id);
      }
    });
  }, [deletedField]);

  useEffect(() => {
    if (!conditions.length && ruleId) {
      setRuleId(null);
      setSentPOST(false);

      // needUpdate();
    }
  }, [conditions]);

  useEffect(
    () => {
      const ruleIndex = ruleId ? rules.findIndex(r => r.id === ruleId) : -1;

      let condition = conditions.map(packCondition);

      condition = condition.filter(Boolean);

      // DELETE if rule is now empty
      if (condition.filter(Boolean).length === 0) {
        if (ruleId && ruleIndex >= 0) {
          fetchAPI(`/forms/v1/rules/${ruleId}`, {
            method: 'DELETE'
          });

          setRuleId(null);
          setSentPOST(false);
        }

        if (ruleIndex >= 0) {
          setRules(dotProp.delete(rules, ruleIndex));
        }

        return;
      }

      if (rootAndOr === 'or') {
        condition.push({ internOp: 'or' });
      }

      condition = JSON.stringify(condition);
      condition = condition.replace(/"\${null}"/gi, 'null');

      const body = {
        formId,
        fieldId,
        condition,
        action: {
          actionType,
          parameters: [
            {
              key: actionType,
              value: fieldId
            }
          ]
        }
      };

      if (ruleId) {
        body.id = ruleId;
      }

      // POST if no ID, PUT if there is
      if (!ruleId) {
        // make sure we only send POST once -- there may be a response pending
        if (!sentPOST) {
          setSentPOST(true);

          fetchAPI('/forms/v1/rules', {
            method: 'POST',
            body,
            handleResponse: r =>
              r
                .text()
                .then(text => (text ? JSON.parse(text) : {}))
                .then(json => {
                  if (r.ok) {
                    return r;
                  } else {
                    return Promise.reject({
                      response: r,
                      json
                    });
                  }
                })
          }).then(r => {
            // get ID from Location header
            const id = r.headers.map.location;
            setRuleId(id);
            body.id = id;

            // we need to run this effect in case there were skipped updates while waiting for POST response
            needUpdate();
          });
        }
      } else {
        ruleId &&
          fetchAPI(`/forms/v1/rules/${ruleId}`, {
            method: 'PUT',
            body
          });
      }

      const index = ruleIndex >= 0 ? ruleIndex : rules.length;
      setRules(dotProp.set(rules, index, body));
    },
    // eslint-disable-next-line
    [lastUpdate]
  );

  const ruleIdProp = rule && rule.id;

  // load state from props
  useEffect(() => {
    if (ruleIdProp && !ruleId) {
      setRuleId(rule.id);
      setActionType(rule.action.actionType);

      const [_conditions, _rootAndOr] = unpackCondition(rule.condition);
      setRootAndOr(_rootAndOr);
      _conditions &&
        _conditions.length &&
        conditionsDispatch({
          type: 'load',
          payload: _conditions
        });
    }
  }, [ruleIdProp, ruleId, rule]);

  useEffect(() => {
    // can't use actionType here as the initial rendering isn't updated
    if (
      !rules.some(_rule =>
        _rule.fieldId === internalId && _rule.action.actionType === rule
          ? rule.action.actionType
          : actionType
      )
    ) {
      createDefaultRule();
    }
  }, []);

  const updateCondition = i => payload => {
    conditionsDispatch({ type: 'update', i, payload });
    needUpdate();
  };

  const remove = id => {
    conditionsDispatch({ type: 'remove', payload: { id } });
    needUpdate();
  };

  return (
    <>
      <ConditionHeader>
        {selectShowHide ? (
          <ShowHide
            uiType={uiType}
            showHide={actionType}
            onChange={e => {
              setActionType(e.target.value);
              needUpdate();
            }}
          />
        ) : (
          <>
            <FlagIcon /> Flag a submission{' '}
          </>
        )}
      </ConditionHeader>
      <Divider />
      {conditions.map((condition, i) => (
        <>
          <ConditionBuilder
            key={i}
            i={i}
            preferSelf={preferSelf}
            update={updateCondition(i)}
            remove={() => remove(condition._id)}
            add={() => conditionsDispatch({ type: 'add' })}
            {...{
              formId,
              internalId,
              condition,
              conditions,
              rootAndOr,
              allowRootAndOrToggle: selectShowHide,
              updateRootAndOr
            }}
          />
        </>
      ))}
      <AddButton
        data-testid={`formbuilder-add-condition`}
        color="primary"
        onClick={() => {
          conditionsDispatch({ type: 'add' });
          needUpdate();
        }}
        startIcon={<AddCircleIcon />}
      >
        Add a condition
      </AddButton>
      <Divider />
    </>
  );
};

export default ConditionalLogic;
